import type {
  ProductRelation,
  ProductRelationType,
  RelationCertainty,
  ValueSource,
} from "@/api/types";
import type { BadgeTone } from "@/components/StatusBadge";

/**
 * Comment une relation se lit à l'écran (TICKET V1.5-07, ADR 0013).
 *
 * Séparé du composant pour être testable : ce sont des règles d'affichage, pas
 * du rendu, et elles ont un cas particulier qui compte — **la direction**.
 * « Accessoire pour » et « A pour accessoire » sont la même ligne en base, lue
 * de deux bouts, et l'écran doit dire la bonne selon le produit qu'on regarde.
 *
 * L'utilisateur ne doit jamais voir `ACCESSORY_FOR`. Traduire un enum n'est pas
 * son travail.
 */

/** Ce que la relation dit, du côté direct puis du côté inverse. */
export const RELATION_LABELS: Record<ProductRelationType, [string, string]> = {
  REPLACED_BY: ["Remplacé par", "Remplace"],
  EQUIVALENT_TO: ["Équivalent à", "Équivalent à"],
  WORKS_WITH: ["Fonctionne avec", "Fonctionne avec"],
  REQUIRES: ["Nécessite", "Est requis par"],
  ACCESSORY_FOR: ["Accessoire pour", "A pour accessoire"],
  CONSUMABLE_FOR: ["Consommable pour", "Consomme"],
  PART_OF: ["Pièce de", "Contient la pièce"],
};

/** Le titre d'un groupe de relations du même type, du côté indiqué. */
export const RELATION_GROUP_LABELS: Record<ProductRelationType, [string, string]> = {
  REPLACED_BY: ["Remplacé par", "Remplace"],
  EQUIVALENT_TO: ["Équivalents", "Équivalents"],
  WORKS_WITH: ["Fonctionne avec", "Fonctionne avec"],
  REQUIRES: ["Nécessite", "Requis par"],
  ACCESSORY_FOR: ["Accessoire pour", "Accessoires"],
  CONSUMABLE_FOR: ["Consommable pour", "Consommables"],
  PART_OF: ["Pièce de", "Pièces"],
};

/** Ce qu'on écrit dans le menu de saisie : le sens direct, à la première personne. */
export const RELATION_CHOICES: { value: ProductRelationType; label: string }[] = [
  { value: "WORKS_WITH", label: "Fonctionne avec…" },
  { value: "REQUIRES", label: "Nécessite…" },
  { value: "ACCESSORY_FOR", label: "Est un accessoire pour…" },
  { value: "CONSUMABLE_FOR", label: "Est un consommable pour…" },
  { value: "PART_OF", label: "Est une pièce de…" },
  { value: "EQUIVALENT_TO", label: "Est équivalent à…" },
  { value: "REPLACED_BY", label: "Est remplacé par…" },
];

export const CERTAINTY_LABELS: Record<RelationCertainty, { label: string; tone: BadgeTone }> = {
  VERIFIED: { label: "Vérifié", tone: "available" },
  APPROVED_EQUIVALENT: { label: "Équivalent approuvé", tone: "available" },
  LIKELY: { label: "Probable", tone: "reserved" },
  INCOMPATIBLE: { label: "Incompatible", tone: "out" },
};

/** Les certitudes qu'un formulaire propose. `VERIFIED` vient de la confirmation. */
export const ASSERTABLE_CERTAINTIES: RelationCertainty[] = [
  "LIKELY",
  "VERIFIED",
  "APPROVED_EQUIVALENT",
  "INCOMPATIBLE",
];

export const SOURCE_LABELS: Record<ValueSource, string> = {
  HUMAN: "saisi",
  BARCODE: "code-barres",
  OCR: "texte lu",
  LOCAL_VISION: "modèle local",
  CLOUD_VISION: "modèle cloud",
  MANUFACTURER: "fiche fabricant",
  CATALOGUE: "catalogue",
  SUPPLIER: "fournisseur",
  WEB: "web",
  MEASURED: "mesuré",
  IMPORT: "import",
  INFERRED: "déduit",
  SYSTEM: "système",
};

/** Les sources qu'un humain invoque à la saisie. `INFERRED` n'en est pas :
 *  une déduction se calcule à la demande, elle ne s'enregistre pas. */
export const ASSERTABLE_SOURCES: ValueSource[] = [
  "HUMAN",
  "MANUFACTURER",
  "CATALOGUE",
  "SUPPLIER",
  "WEB",
];

/** « Accessoire pour » ou « A pour accessoire », selon le côté d'où on regarde. */
export function relationLabel(type: ProductRelationType, reversed: boolean): string {
  const [forward, backward] = RELATION_LABELS[type];
  return reversed ? backward : forward;
}

export function relationGroupLabel(type: ProductRelationType, reversed: boolean): string {
  const [forward, backward] = RELATION_GROUP_LABELS[type];
  return reversed ? backward : forward;
}

/**
 * Les relations regroupées par ce qu'elles disent, dans l'ordre d'affichage.
 *
 * Groupé par (type, sens) et non par type seul : « Accessoires » et
 * « Accessoire pour » sont deux listes différentes sur la même fiche, et les
 * fondre ferait dire à l'écran l'inverse de ce que la ligne affirme.
 */
export function groupRelations(
  relations: ProductRelation[],
): { key: string; title: string; items: ProductRelation[] }[] {
  const groups = new Map<string, { key: string; title: string; items: ProductRelation[] }>();
  for (const relation of relations) {
    const key = `${relation.relation_type}:${relation.reversed ? "r" : "f"}`;
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(relation);
    } else {
      groups.set(key, {
        key,
        title: relationGroupLabel(relation.relation_type, relation.reversed),
        items: [relation],
      });
    }
  }
  return [...groups.values()].sort((left, right) => left.title.localeCompare(right.title, "fr"));
}
